/**
 * Products across, features down. Lifted from the v1 brainstorm matrix: sticky
 * first column, rotated headers, ● ships / ◐ behind a paid plan (named inline)
 * / ? nobody read their pages / · not seen. Below the table, what is missing
 * and who finds it, each with a Dig button that carries the directive.
 */
import { useMemo, useState, useCallback } from 'react';
import { CARD } from './tableLayout';
import type { BoardroomFeatureMatrix, BoardroomMatrixCell, BoardroomMatrixGroup, BoardroomMatrixProduct } from '../../../types';
import MissingList, { type OnDig } from './MissingList';
import SpecTable from './SpecTable';
import DataTable from './DataTable';
import ColumnsStatus from './ColumnsStatus';
import type { ColumnChange } from './columnChange';
import SplitIntoKinds from './SplitIntoKinds';
import type { Column } from './tableColumns';
import { SIDEWAYS_CHARS } from './TableGrid';
import { useReportSeen, type SeenMarks } from '../useSeen';
import NewHere from './NewHere';
import {
  groupItemId,
  matrixCell,
  regroupByCompany,
  rememberMatrixGrouping,
  storedMatrixGrouping,
  type MatrixGrouping,
} from './reportsView';

/** `ships/total` — what the header's count says. One definition, because the
 *  band is measured off a string and drawn from a node, and the two drifting
 *  apart is exactly what clipped it. */
const tally = (p: BoardroomMatrixProduct, group: BoardroomMatrixGroup) =>
  `${p.ships}/${group.rows.length}`;

/** A title cut to leave the count room inside the band. */
const shortTitle = (title: string, count: string) => {
  const room = SIDEWAYS_CHARS - count.length - 2; // the gap, and the tick
  return title.length > room ? title.slice(0, Math.max(4, room - 1)) + '…' : title;
};

function Cell({ cell, product, feature }: { cell: BoardroomMatrixCell; product: BoardroomMatrixProduct; feature: string }) {
  const title =
    cell.state === 'ships'
      ? `${product.title} - ${feature}`
      : cell.state === 'gated'
        ? `${product.title} - ${feature}, in the ${cell.plan} plan`
        : cell.state === 'unread'
          ? `${product.title} - nobody has read their pages yet, so this is unknown`
          : `${product.title} - not seen on the pages we read`;
  const tone =
    cell.state === 'ships'
      ? 'text-emerald-600 dark:text-emerald-400'
      : cell.state === 'gated'
        ? 'text-amber-600 dark:text-amber-400'
        : cell.state === 'unread'
          ? 'text-gray-300 dark:text-gray-600'
          : 'text-gray-200 dark:text-gray-500';
  const glyph = cell.state === 'ships' ? '●' : cell.state === 'gated' ? '◐' : cell.state === 'unread' ? '?' : '·';
  return (
    <span title={title} className={`${tone} whitespace-nowrap`} data-state={cell.state}>
      {glyph}
      {cell.state === 'gated' && <span className="ml-0.5 text-[10px]">{cell.plan}</span>}
    </span>
  );
}

type MatrixRow = BoardroomMatrixGroup['rows'][number];

/** ONE DESCRIPTION PER COLUMN: the feature down the side, then a column per
 *  product. This used to be two complete `<table>` bodies, one per axis, each
 *  a transpose of the other — so a change to a cell had to be made in both,
 *  and a change to only one was invisible until somebody flipped. The cell was
 *  already the same either way; now so is everything around it.
 *
 *  The counts ride the NAMES rather than sitting in a column of their own.
 *  A column of ships-per-feature would become a ROW of them when flipped, and
 *  the products' own count would have nowhere to go — which is why each
 *  orientation used to show a different one. Beside the name, both survive the
 *  turn, and the accessible name of each button stays the plain title. */
function groupColumns(group: BoardroomMatrixGroup, onOpen: (itemId: number) => void): Column<MatrixRow>[] {
  return [
    {
      key: '__feature',
      label: 'Feature',
      flipLabel: 'Product',
      always: true,
      width: 30,
      value: r => r.title,
      cell: r => (
        <>
          <button
            type="button"
            onClick={() => onOpen(r.feature_id)}
            className="text-xs text-gray-800 dark:text-gray-200 hover:underline text-left"
          >
            {r.title}
          </button>
          <span
            className="ms-1.5 font-mono text-[11px] text-gray-500"
            title={`${r.ships} of ${r.field_size} products ship it`}
          >
            {r.ships}/{r.field_size}
          </span>
        </>
      ),
    },
    ...group.products.map(p => ({
      key: `product-${p.id}`,
      label: p.title,
      itemId: p.id,
      vertical: true,
      value: (r: MatrixRow) => matrixCell(r.cells, p).state,
      cellId: (r: MatrixRow) => `cell-${r.feature_id}-${p.id}`,
      cellClass: () => 'text-center px-1',
      /* Plain inline content, not a flex box: turned on its side a flex row
         lays out down the page and the header falls apart. */
      /* THE COUNT MUST SURVIVE THE CLIP. The turned run is cut at the band's
         end and the count sits at that end, so a long title ate it and left
         `7/3` and `2/` along the top — a number that is worse than absent
         half-printed. The title is trimmed to leave room for the count rather
         than the other way round: which product a column is, is also written
         in the tooltip and the cells below, and `31/31` is nowhere else. */
      measure: `${shortTitle(p.title, tally(p, group))} ${tally(p, group)}`,
      head: (
        <>
          <button
            type="button"
            onClick={() => onOpen(p.id)}
            title={p.read ? `${p.title}${p.company ? ` · ${p.company.title}` : ''}` : `${p.title} - nobody has read their pages yet`}
            className={`text-[11px] hover:underline ${
              p.read ? 'text-gray-500 dark:text-gray-400' : 'text-gray-300 dark:text-gray-600 italic'
            }`}
          >
            {shortTitle(p.title, tally(p, group))}
            {p.verdict === 'HOLDS' && <span className="text-emerald-500"> ✓</span>}
          </button>
          <span className="ms-1.5 font-mono text-[10px] text-gray-500" title={`${p.ships} of ${group.rows.length} features`}>
            {tally(p, group)}
          </span>
        </>
      ),
      cell: (r: MatrixRow) => (
        <Cell cell={matrixCell(r.cells, p)} product={p} feature={r.title} />
      ),
    })),
  ];
}

function GroupTable({
  group,
  onOpen,
  onDig,
  digging,
  roomName,
  seen,
  onSplitType,
  onUndoSplit,
  splitting,
}: {
  group: BoardroomMatrixGroup;
  onOpen: (itemId: number) => void;
  onDig: OnDig;
  digging: boolean;
  roomName?: string;
  onSplitType?: (typeId: number) => void;
  onUndoSplit?: (typeId: number) => void;
  splitting?: boolean;
  /** The REPORT'S tracker: this report draws a grid per group, and a tracker
   *  per grid pruned every mark that belonged to the others. */
  seen: SeenMarks;
}) {
  const columns = useMemo(() => groupColumns(group, onOpen), [group, onOpen]);
  const stakes = group.rows.filter(r => r.table_stakes).length;
  /* THE HEADING AND THE CONTROLS ON ONE LINE. Drawn separately, the table's
     own controls landed under the card's heading and above the grid — in the
     middle of the card, belonging to neither. `DataTable` takes the heading
     and the extras as slots, so the order is the table's to get right. */
  const heading = (
    <span className="flex items-baseline gap-2">
      {/* WHERE YOU ARE, not just what you are looking at. A table called
          "Report Platforms" says nothing about the field it is a slice of. */}
      {(group.path?.length ?? 0) > 1 && (
        <span className="text-xs text-gray-400" data-testid={`group-path-${group.key}`}>
          {group.path!.slice(0, -1).map(t => t.title).join(' › ')} ›
        </span>
      )}
      {/* The heading is the company or the type itself: open its profile. */}
      {groupItemId(group) ? (
        <button type="button" onClick={() => onOpen(groupItemId(group)!)} className="text-sm font-semibold text-gray-800 dark:text-gray-100 hover:underline text-left" data-testid={`group-title-${group.key}`}>
          {group.label}
        </button>
      ) : (
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{group.label}</span>
      )}
      <span className="text-xs text-gray-400">
        {group.products.length === 0
          ? 'the kinds below'
          : `${group.products.length} product${group.products.length === 1 ? '' : 's'} · ${group.rows.length} feature${group.rows.length === 1 ? '' : 's'}`}
        {/* IS THIS TABLE COMPARING ANYTHING? The share of its products the
            best-covered column reaches. A number you can read across cards
            beats a colour you have to hover to decode. */}
        {group.cohesion !== undefined && group.products.length > 1 && (
          <span
            title={`The most widely shared column reaches ${Math.round(group.cohesion * 100)}% of these products. Low means they are not really being compared on anything - which is what several markets sharing one heading looks like.`}
            data-testid={`cohesion-${group.key}`}
          >
            {' · '}
            <span className={group.cohesion < 1 / 3 ? 'text-amber-700 dark:text-amber-400' : ''}>
              {Math.round(group.cohesion * 100)}% share a column
            </span>
          </span>
        )}
      </span>
    </span>
  );
  const extras = (
    <>
      <SplitIntoKinds group={group} onSplit={onSplitType} onUndo={onUndoSplit} splitting={splitting} />
    </>
  );

  return (
    <div
      className={CARD}
      data-testid={`group-${group.key}`}
      /* A SUB-GENRE SITS UNDER ITS GENRE. The groups arrive flat with a depth
         rather than nested, because the client rebuilds this array to regroup
         by company — and an indent is all the tree a reader needs. */
      style={group.depth ? { marginLeft: group.depth * 16 } : undefined}
      data-depth={group.depth || undefined}
    >
      {group.products.length === 0 && (
        <div className="flex items-baseline gap-2">
          {heading}
          <span className="ml-auto flex items-baseline gap-2">{extras}</span>
        </div>
      )}
      {/* A HEADING, NOT AN EMPTY TABLE. Once every product of a broad type
          sits in one of its kinds, the type itself holds nobody — and a card
          with a blank grid in it says the opposite of what happened. */}
      {group.products.length === 0 ? null : (
      <DataTable
        title={heading}
        toolbar={extras}
        table={`matrix-${group.key}`}
        file={`features-${group.label}`}
        room={roomName}
        columns={columns}
        rows={group.rows}
        rowKey={r => `feature-${r.feature_id}`}
        rowItemId={r => r.feature_id}
        seen={seen}
        // A column per product: no room to declare widths, or to write the
        // names across the top.
        defaultMode="long"
        verticalHeads
        /* Table-stakes features come first and read as their own band, so a
           product's gaps show as a run of dots near the left. */
        section={
          stakes > 0
            ? r =>
                r.table_stakes
                  ? `Table stakes · ${stakes} feature${stakes === 1 ? '' : 's'} most of the field ships`
                  : 'Not universal · where products differ'
            : undefined
        }
        empty="No features recorded for these products yet."
      />
      )}
    </div>
  );
}

/** One market's comparison, in the card the features view already uses.
 *
 *  The heading, the lineage and the indent are the same as `GroupTable`'s,
 *  because a reader flipping between the two tabs is looking at one board
 *  sliced two ways and should not have to re-learn where they are. */
const NO_IDS: readonly number[] = [];

function SpecTableCard({
  group,
  onOpen,
  unmapped,
  working,
  roomName,
  seen,
  featuresOf,
  onSplitType,
  onUndoSplit,
  splitting,
}: {
  group: BoardroomMatrixGroup;
  onOpen: (itemId: number) => void;
  /** A product's features, so a feature that lands lights its product here. */
  featuresOf: (productId: number) => readonly number[];
  /** Only to say how many phrases are still just phrases; the offer that
   *  closes that lives once, in the report's control strip. */
  unmapped: number;
  working?: boolean;
  roomName?: string;
  seen: SeenMarks;
  onSplitType?: (typeId: number) => void;
  onUndoSplit?: (typeId: number) => void;
  splitting?: boolean;
}) {
  /* One line: the heading, then the table's own controls. Same shape as the
     features view, because a reader flipping between the two tabs is looking
     at one board sliced two ways. */
  const heading = (
    <span className="flex items-baseline gap-2">
      {(group.path?.length ?? 0) > 1 && (
        <span className="text-xs text-gray-400">
          {group.path!.slice(0, -1).map(t => t.title).join(' › ')} ›
        </span>
      )}
      {groupItemId(group) ? (
        <button
          type="button"
          onClick={() => onOpen(groupItemId(group)!)}
          className="text-sm font-semibold text-gray-800 dark:text-gray-100 hover:underline text-left"
          data-testid={`spec-group-title-${group.key}`}
        >
          {group.label}
        </button>
      ) : (
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{group.label}</span>
      )}
      <span className="text-xs text-gray-400">
        {group.products.length === 0
          ? 'the kinds below'
          : `${group.products.length} product${group.products.length === 1 ? '' : 's'}`}
      </span>
    </span>
  );

  return (
    <div
      className="shrink-0"
      data-testid={`spec-group-${group.key}`}
      style={group.depth ? { marginLeft: group.depth * 16 } : undefined}
    >
      {group.products.length === 0 && (
        <div className="flex items-baseline gap-2 px-1">
          {heading}
          <span className="ml-auto">
            <SplitIntoKinds group={group} onSplit={onSplitType} onUndo={onUndoSplit} splitting={splitting} />
          </span>
        </div>
      )}
      {group.products.length === 0 ? null : (
      <SpecTable
        title={heading}
        toolbar={<SplitIntoKinds group={group} onSplit={onSplitType} onUndo={onUndoSplit} splitting={splitting} />}
        table={`specs-${group.key}`}
        rows={group.specs ?? []}
        products={group.products}
        onOpen={onOpen}
        unmapped={unmapped}
        working={working}
        room={roomName}
        seen={seen}
        featuresOf={featuresOf}
      />
      )}
    </div>
  );
}

export default function FeatureMatrixReport({
  data,
  onOpen,
  onDig,
  digging,
  unseen,
  onSeen,
  roomName,
  columnChange = null,
  onUndoColumns,
  onSplitType,
  onUndoSplit,
  splittingType,
}: {
  data: BoardroomFeatureMatrix;
  onOpen: (itemId: number) => void;
  onDig: OnDig;
  /** A run is going: the buttons say so instead of starting a second one. */
  digging: boolean;
  /** Ids that changed and have not been looked at. */
  unseen?: ReadonlySet<number>;
  /** Ids now on screen — the caller drops them from the badge. */
  onSeen?: (ids: number[]) => void;
  /** For the download filename, so a saved table says which room it is. */
  roomName?: string;
  /** What the room last did to its columns (`columnChange.ts`). */
  columnChange?: ColumnChange | null;
  /** Put the columns back as they were before that change. */
  onUndoColumns?: (frameId: number) => Promise<void> | void;
  /** Split a type that is doing the work of several markets. */
  onSplitType?: (typeId: number) => void;
  /** Put the last split back. */
  onUndoSplit?: (typeId: number) => void;
  splittingType?: boolean;
}) {
  const [grouping, setGrouping] = useState<MatrixGrouping>(() => storedMatrixGrouping());
  // WHAT A ROW MEANS. "Specs" reads the sheet's columns with values in the
  // cells — the comparable view; "features" is the older one, a row per
  // recorded phrase. Specs leads when the room has named any columns, because
  // a table of singleton phrases compares nothing.
  const specRows = data.specs ?? [];
  // THE COUNT IS BOUND HERE because this is where it lives. The re-read covers
  // every live phrase in the room, filed or not — `unmapped` is the delta and
  // would name the wrong job in the confirm.
  // WHAT THE FIT COVERS. Not `unmapped` — that is "no facts at all", which
  // misses every phrase filed under a sheet that has since changed. Older
  // rooms predate the field, so fall back rather than reading nought.
  const unfitted = data.unfitted ?? data.unmapped ?? 0;
  // THE FIT'S DISAGREEMENT, which only the re-work can act on.
  // The choice is REMEMBERED ONLY ONCE MADE. A `useState` initialiser runs on
  // the first render, and the report arrives after it — so seeding the default
  // from `data` there pinned the view to "features" forever on any room whose
  // matrix had not loaded yet, whatever the sheet said a moment later.
  const [chosen, setChosen] = useState<'specs' | 'features' | null>(null);
  // A REAL COLUMN, not any row. A fact recorded off the sheet is a row too, and
  // one of them opened the matrix on a sheet with no columns: room 40 showed one
  // row under "69 new here" while its 113 features sat in the other view.
  const view = chosen ?? (specRows.some(r => r.on_sheet) ? 'specs' : 'features');
  const groups = useMemo(() => (grouping === 'company' ? regroupByCompany(data) : data.groups), [data, grouping]);
  /* The specs tab reads its own groups, because regrouping by company would
     hand it product sets with no `specs` computed for them. Falls back to one
     table over the whole room for a payload from before per-group specs, so an
     open tab does not go blank on deploy. */
  const specGroups = useMemo<BoardroomMatrixGroup[]>(() => {
    const withSpecs = data.groups.filter(g => g.specs !== undefined);
    if (withSpecs.length) return withSpecs;
    return [
      {
        key: 'all',
        solution_type_id: null,
        label: 'Every product on the board',
        products: data.groups.flatMap(g => g.products),
        rows: [],
        dig: null,
        specs: data.specs ?? [],
      },
    ];
  }, [data]);
  const empty = data.totals.products === 0;
  const pick = (g: MatrixGrouping) => {
    setGrouping(g);
    rememberMatrixGrouping(g);
  };
  // ONE TRACKER for every grid and the questions under them, and it knows the
  // feature rows WHATEVER VIEW IS OPEN. A feature is a row of the features
  // view only — the spec sheet's rows are attributes — so counting only what
  // the open view can show meant four features landing while the sheet was up
  // were pruned as "no row here" and their marks thrown away.
  const seen = useReportSeen(unseen, onSeen, [
    ...groups.flatMap(g => g.rows.map(r => r.feature_id)),
    ...data.missing.map(m => m.item_id),
  ]);
  // WHICH FEATURES EACH PRODUCT SHIPS, across every market it appears in: the
  // sheet has no feature rows, so a new feature is marked on its product.
  const featuresByProduct = useMemo(() => {
    const m = new Map<number, number[]>();
    for (const g of data.groups) {
      for (const r of g.rows) {
        for (const [pid, cell] of Object.entries(r.cells)) {
          if (cell.state !== 'ships' && cell.state !== 'gated') continue;
          const list = m.get(Number(pid)) ?? [];
          if (!list.includes(r.feature_id)) list.push(r.feature_id);
          m.set(Number(pid), list);
        }
      }
    }
    return m;
  }, [data]);
  const featuresOf = useCallback((pid: number) => featuresByProduct.get(pid) ?? NO_IDS, [featuresByProduct]);
  // A BUTTON THAT CAN REACH THEM. On the sheet the marks ride the products;
  // a feature that hangs on no product has nothing here to scroll to, so only
  // then does the press switch to the view that draws it.
  const jump = () => {
    if (!seen.next() && view === 'specs') setChosen('features');
  };

  return (
    <div className="flex flex-col gap-3 min-h-0">
      {data.truncated && (
        <div className="text-xs text-amber-700 dark:text-amber-300" role="status">
          Showing the first 4,000 items on the board · narrow with a filter to see the rest.
        </div>
      )}
      {!empty && (
        <div className="shrink-0 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <span>Columns grouped by</span>
          <div role="radiogroup" aria-label="Columns grouped by" className="inline-flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
            {(['type', 'company'] as MatrixGrouping[]).map(g => (
              <button
                key={g}
                type="button"
                role="radio"
                aria-checked={grouping === g}
                onClick={() => pick(g)}
                className={`px-2 py-0.5 border-r last:border-r-0 border-gray-300 dark:border-gray-600 ${
                  grouping === g ? 'bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {g === 'type' ? 'solution type' : 'company'}
              </button>
            ))}
          </div>
          {view === 'features' && (
            <span className="ml-auto text-gray-400">
              <span className="text-emerald-600">●</span> ships · <span className="text-amber-600">◐</span> in a paid plan · ? not read · · not seen
            </span>
          )}
        </div>
      )}
      {!empty && (
        <div className="shrink-0 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <span>Rows are</span>
          <div role="radiogroup" aria-label="Rows are" className="inline-flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
            {(['specs', 'features'] as const).map(v => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={view === v}
                onClick={() => setChosen(v)}
                title={
                  v === 'specs'
                    ? 'What this market is compared on, with each product\'s value'
                    : 'One row per feature as it was recorded, in the page\'s own words'
                }
                className={`px-2 py-0.5 border-r last:border-r-0 border-gray-300 dark:border-gray-600 ${
                  view === v ? 'bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {v === 'specs' ? 'what they are compared on' : 'features as recorded'}
              </button>
            ))}
          </div>
          {/* THE OFFER BELONGS WHERE THE PROBLEM IS. This view is the one
              showing 192 rows that each read 1/69; the spec view is where you
              land once the columns exist. Putting the button only there filed
              it behind a tab nobody opens until it has been pressed. */}
          {/* Not gated on the sheet being empty: the offer CHANGES once the
              columns exist — from naming them to fitting what has landed
              since — and gating it on emptiness made the second one, which is
              the one you need every dig, unreachable. */}
          <ColumnsStatus
            unfitted={unfitted}
            hasSheet={specRows.length > 0}
            change={columnChange}
            onUndo={onUndoColumns}
          />
        </div>
      )}
      <NewHere count={seen.waitingHere} glowing={seen.glowing} onJump={jump} onClearAll={seen.readAll} />
      {view === 'specs' ? (
        /* A COMPARISON PER MARKET. This used to flatten every group into one
           table of every product in the room — which is why the type's name
           vanished on this tab, and why a column read 10/119 where within the
           twelve products that actually compete on it, it is table stakes. */
        specGroups.map(g => (
          <SpecTableCard
            key={g.key}
            group={g}
            onOpen={onOpen}
            unmapped={g.key === specGroups[0].key ? data.unmapped ?? 0 : 0}
            roomName={roomName}
            seen={seen}
            featuresOf={featuresOf}
            onSplitType={onSplitType}
            onUndoSplit={onUndoSplit}
            splitting={splittingType}
          />
        ))
      ) : (
        groups.map(g => (
          <GroupTable
            key={g.key}
            group={g}
            onOpen={onOpen}
            onDig={onDig}
            digging={digging}
            roomName={roomName}
            seen={seen}
            onSplitType={onSplitType}
            onUndoSplit={onUndoSplit}
            splitting={splittingType}
          />
        ))
      )}
      <MissingList missing={data.missing} empty={empty} onOpen={onOpen} onDig={onDig} digging={digging} seen={seen} />
    </div>
  );
}
